import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from './Modal'
import MessageModal from './MessageModal'
import ConfirmModal from './ConfirmModal'
import {
  useCreateUser,
  useUpdateUser,
  useAllowedNetworksCatalog,
  useUserAllowedNetworks,
  useUpdateUserAllowedNetworks,
  useResetPassword,
  useDeleteUser,
} from '../../hooks/useUsers'
import { Check, KeyRound, MailWarning, Trash2 } from 'lucide-react'
import PhoneInput, { validatePhone } from '../PhoneInput'
import { useRole } from '../../hooks/useRole'
import { useAuth } from '../../contexts/AuthContext'

const roleOptions = [
  { value: 'Owner', label: 'Owner' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Operator', label: 'Operator' },
  { value: 'Viewer', label: 'Viewer' },
]

export default function UserModal({ isOpen, onClose, user = null }) {
  const { t } = useTranslation()
  const isEditing = !!user
  const createUser = useCreateUser()
  const updateUser = useUpdateUser()
  const updateUserAllowedNetworks = useUpdateUserAllowedNetworks()
  const resetPassword = useResetPassword()
  const deleteUser = useDeleteUser()
  const { canManageUsers } = useRole()
  const { user: authUser } = useAuth()
  const { data: networkCatalog = [] } = useAllowedNetworksCatalog()
  const { data: userAssignments = [] } = useUserAllowedNetworks(user?.id)

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    role: 'Viewer',
    vpnEnabled: false,
    enabled: true,
  })

  const [selectedNetworkIds, setSelectedNetworkIds] = useState(new Set())
  const [errors, setErrors] = useState({})
  const [activeTab, setActiveTab] = useState('basic') // 'basic' | 'networks'
  const [messageModal, setMessageModal] = useState({ isOpen: false, type: 'info', message: '' })
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null })

  // Atualizar formData quando o usuário mudar ou o modal abrir
  useEffect(() => {
    if (isOpen) {
      if (user) {
        const st = (user.status || '').toLowerCase()
        setFormData({
          name: user.name || '',
          email: user.email || '',
          phoneNumber: user.phoneNumber || '',
          role: user.role || 'Viewer',
          vpnEnabled: user.vpnEnabled || false,
          enabled: st === 'active',
        })
      } else {
        setFormData({
          name: '',
          email: '',
          phoneNumber: '',
          role: 'Viewer',
          vpnEnabled: false,
          enabled: true,
        })
      }
      setActiveTab('basic')
      setErrors({})
    }
  }, [isOpen, user])

  useEffect(() => {
    if (isEditing && userAssignments.length > 0) {
      setSelectedNetworkIds(new Set(userAssignments.map((r) => r.remoteNetworkId)))
    } else {
      setSelectedNetworkIds(new Set())
    }
  }, [isEditing, userAssignments])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    const newValue = type === 'checkbox' ? checked : value
    setFormData((prev) => ({ ...prev, [name]: newValue }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }))
    }
  }

  const toggleAllowedNetwork = (remoteNetworkId) => {
    setSelectedNetworkIds((prev) => {
      const next = new Set(prev)
      if (next.has(remoteNetworkId)) next.delete(remoteNetworkId)
      else next.add(remoteNetworkId)
      return next
    })
  }

  const handleSelectAllNetworks = () => {
    if (selectedNetworkIds.size === networkCatalog.length) {
      setSelectedNetworkIds(new Set())
    } else {
      setSelectedNetworkIds(new Set(networkCatalog.map((r) => r.remoteNetworkId)))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) {
      newErrors.name = t('users.modal.nameRequired')
    }
    if (!formData.email.trim()) {
      newErrors.email = t('users.modal.emailRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('users.modal.emailInvalid')
    }
    if (formData.phoneNumber) {
      const perr = validatePhone(formData.phoneNumber)
      if (perr) newErrors.phoneNumber = t(perr)
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) return

    try {
      if (isEditing) {
        await updateUser.mutateAsync({
          id: user.id,
          data: {
            name: formData.name,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            role: formData.role,
            vpnEnabled: formData.vpnEnabled,
            status: formData.enabled ? 'active' : 'disabled',
          },
        })
        
        await updateUserAllowedNetworks.mutateAsync({
          id: user.id,
          data: {
            remoteNetworkIds: Array.from(selectedNetworkIds),
          },
        })
      } else {
        const created = await createUser.mutateAsync({
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          role: formData.role,
          vpnEnabled: formData.vpnEnabled,
          enabled: formData.enabled,
        })
        onClose()
        if (!formData.enabled) {
          setMessageModal({
            isOpen: true,
            type: 'success',
            message: t('users.modal.successCreatedDisabled'),
          })
        } else {
          const emailFail =
            created?.emailDeliveryFailedAt != null || created?.EmailDeliveryFailedAt != null
          const emailMsg =
            created?.emailDeliveryFailureMessage || created?.EmailDeliveryFailureMessage || ''
          if (emailFail) {
            setMessageModal({
              isOpen: true,
              type: 'warning',
              message: t('users.modal.successCreatedEmailFail', { msg: emailMsg }),
            })
          } else {
            setMessageModal({
              isOpen: true,
              type: 'success',
              message: t('users.modal.successCreatedEmailSent'),
            })
          }
        }
        return
      }
      onClose()
      setMessageModal({
        isOpen: true,
        type: 'success',
        message: t('users.modal.successUpdated'),
      })
    } catch (error) {
      console.error('Erro ao salvar usuário:', error)
      setMessageModal({
        isOpen: true,
        type: 'error',
        message: error.message || t('users.modal.saveError'),
      })
    }
  }

  const handleResetPassword = () => {
    if (!user?.id) return

    setConfirmModal({
      isOpen: true,
      message: t('users.modal.resetPasswordConfirm', { name: user.name }),
      onConfirm: async () => {
        try {
          const result = await resetPassword.mutateAsync(user.id)
          const sent = result?.emailSent !== false && result?.EmailSent !== false
          setMessageModal({
            isOpen: true,
            type: sent ? 'success' : 'warning',
            message: sent
              ? result?.message || t('users.modal.resetPasswordSuccess')
              : (result?.message || t('users.modal.resetPasswordSendFailed')),
          })
        } catch (error) {
          console.error('Erro ao resetar senha:', error)
          setMessageModal({
            isOpen: true,
            type: 'error',
            message: error.message || t('users.modal.resetPasswordError'),
          })
        }
      },
    })
  }

  const canDeleteThisUser = isEditing && canManageUsers && user?.userId !== (authUser?.id || authUser?.Id)

  const handleDelete = () => {
    if (!user?.id) return
    setConfirmModal({
      isOpen: true,
      message: t('users.modal.deleteConfirm', { name: user.name || user.email }),
      onConfirm: async () => {
        try {
          await deleteUser.mutateAsync(user.id)
          onClose()
          setMessageModal({ isOpen: true, type: 'success', message: t('users.modal.deleteSuccess') })
        } catch (error) {
          console.error('Erro ao excluir usuário:', error)
          setMessageModal({
            isOpen: true,
            type: 'error',
            message: error.message || t('users.modal.deleteError'),
          })
        }
      },
    })
  }

  const networksByRouter = networkCatalog.reduce((acc, row) => {
    if (!acc[row.routerId]) {
      acc[row.routerId] = { routerName: row.routerName, items: [] }
    }
    acc[row.routerId].items.push(row)
    return acc
  }, {})

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('users.modal.titleEdit') : t('users.modal.titleNewModal')}
      className="max-w-4xl"
    >
      {/* Tabs */}
      {isEditing && (
        <div className="flex gap-2 mb-4 border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTab('basic')}
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'basic'
                ? 'text-theme-primary border-b-2 border-theme-primary'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('users.modal.basicInfoTab')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('networks')}
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'networks'
                ? 'text-theme-primary border-b-2 border-theme-primary'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('users.modal.networksTabTitle', { count: selectedNetworkIds.size })}
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {isEditing &&
          (user?.emailDeliveryFailedAt != null || user?.EmailDeliveryFailedAt != null) &&
          (user?.emailDeliveryFailureMessage || user?.EmailDeliveryFailureMessage) && (
            <div
              className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900"
              role="alert"
            >
              <MailWarning className="h-6 w-6 flex-shrink-0 text-amber-600" />
              <div className="min-w-0 text-sm">
                <p className="font-semibold text-amber-800">{t('users.modal.emailDeliveryFailureTitle')}</p>
                <p className="mt-1 whitespace-pre-wrap break-words text-amber-900/90">
                  {user.emailDeliveryFailureMessage || user.EmailDeliveryFailureMessage}
                </p>
                <p className="mt-2 text-xs text-amber-800/80">
                  {t('users.modal.emailDeliveryFailureHint')}
                </p>
              </div>
            </div>
          )}
        {/* Informações Básicas - Mostrar apenas quando não está editando ou quando a aba 'basic' está ativa */}
        {(!isEditing || activeTab === 'basic') && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('users.modal.fieldName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
                placeholder={t('users.modal.namePlaceholder')}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('users.modal.fieldEmail')} <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`input w-full ${errors.email ? 'border-red-500' : ''}`}
                placeholder={t('users.modal.emailPlaceholder')}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {t('users.modal.emailLoginHint')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('users.modal.fieldPhone')}
              </label>
              <PhoneInput
                value={formData.phoneNumber}
                onChange={(v) => {
                  setFormData((prev) => ({ ...prev, phoneNumber: v }))
                  if (errors.phoneNumber) setErrors((prev) => ({ ...prev, phoneNumber: null }))
                }}
                placeholder={t('users.modal.phonePlaceholder')}
                searchPlaceholder={t('phone.searchCountry')}
                error={errors.phoneNumber}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('users.modal.fieldRole')} <span className="text-red-500">*</span>
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="input w-full"
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="enabled"
                  checked={formData.enabled}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 text-theme-primary rounded focus:ring-theme-primary"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {t('users.modal.enabledLabel')}
                  </span>
                  <p className="mt-1 text-xs text-gray-600">
                    {t('users.modal.enabledHint')}
                  </p>
                </div>
              </label>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="vpnEnabled"
                  checked={formData.vpnEnabled}
                  onChange={handleChange}
                  className="w-4 h-4 text-theme-primary rounded focus:ring-theme-primary"
                />
                <span className="text-sm font-medium text-gray-700">
                  {t('users.modal.vpnEnabledLabel')}
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                {t('users.modal.vpnEnabledHint')}
              </p>
              {isEditing && (
                <p className="mt-1 text-xs text-gray-600">
                  {t('users.modal.vpnAccessStatus', {
                    status: formData.vpnEnabled
                      ? t('users.modal.vpnAccessActive')
                      : t('users.modal.vpnAccessInactive'),
                  })}
                </p>
              )}
            </div>

            {/* Botão de resetar senha (apenas ao editar) */}
            {isEditing && (
              <div className="border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={handleResetPassword}
                  className="btn btn-secondary w-full flex items-center justify-center gap-2"
                  disabled={resetPassword.isPending}
                >
                  <KeyRound className="w-4 h-4" />
                  {resetPassword.isPending ? t('users.modal.resetPasswordSending') : t('users.modal.resetPassword')}
                </button>
                <p className="mt-2 text-xs text-gray-500 text-center">
                  {t('users.modal.resetPasswordHint')}
                </p>
              </div>
            )}
          </>
        )}

        {isEditing && activeTab === 'networks' && (
          <div className="space-y-4 border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('users.modal.networksTitle')}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t('users.modal.networksSubtitle')}
                </p>
              </div>
              <button
                type="button"
                onClick={handleSelectAllNetworks}
                className="btn btn-secondary text-sm"
              >
                {selectedNetworkIds.size === networkCatalog.length
                  ? t('users.modal.networksDeselectAll')
                  : t('users.modal.networksSelectAll')}
              </button>
            </div>

            {networkCatalog.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>{t('users.modal.networksEmpty')}</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                {Object.entries(networksByRouter).map(([routerId, { routerName, items }]) => (
                  <div key={routerId} className="border-b border-gray-200 last:border-b-0">
                    <div className="bg-gray-50 px-4 py-2 font-medium text-sm text-gray-700 sticky top-0">
                      {routerName}
                    </div>
                    <div className="divide-y divide-gray-100">
                      {items.map((row) => {
                        const id = row.remoteNetworkId
                        const isSelected = selectedNetworkIds.has(id)
                        return (
                          <label
                            key={id}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleAllowedNetwork(id)}
                              className="w-4 h-4 text-theme-primary rounded focus:ring-theme-primary"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-sm text-gray-900">
                                {row.networkCidr}
                              </div>
                              {row.description && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {row.description}
                                </div>
                              )}
                            </div>
                            {isSelected && (
                              <Check className="w-5 h-5 text-theme-primary" />
                            )}
                          </label>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between items-center gap-3 pt-4 border-t border-gray-200">
          <div>
            {canDeleteThisUser && (
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={deleteUser.isPending}
              >
                <Trash2 className="w-4 h-4" />
                {deleteUser.isPending ? t('users.modal.deleting') : t('users.modal.delete')}
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={createUser.isPending || updateUser.isPending}
            >
              {t('users.modal.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createUser.isPending || updateUser.isPending}
            >
              {createUser.isPending || updateUser.isPending
                ? t('users.modal.saving')
                : isEditing
                ? t('users.modal.save')
                : t('users.modal.create')}
            </button>
          </div>
        </div>
      </form>

      {/* Modal de Mensagem */}
      <MessageModal
        isOpen={messageModal.isOpen}
        onClose={() => setMessageModal({ isOpen: false, type: 'info', message: '' })}
        type={messageModal.type}
        message={messageModal.message}
      />

      {/* Modal de Confirmação */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm || (() => {})}
        message={confirmModal.message}
      />
    </Modal>
  )
}

